import { Injectable } from '@angular/core';
import { ErrorHandlerService } from '@examdojo/core/error-handling';
import { SupabaseSelectQueryConfig } from '@examdojo/core/supabase';
import { mapToVoid, rethrowError } from '@examdojo/rxjs';
import { finalize, map, Observable, tap } from 'rxjs';
import { SourceHttpService } from './source-http.service';
import {
  RawSourceItem,
  SourceCreateModel,
  SourceHttpModel,
  SourceListItemHttpModel,
  SourceStoreModel,
  SourceUpdateModel,
} from './source.model';
import { SourceQuery } from './source.query';
import { SourceStore } from './source.store';

@Injectable({ providedIn: 'root' })
export class SourceService {
  constructor(
    private readonly sourceHttpService: SourceHttpService,
    private readonly store: SourceStore,
    private readonly query: SourceQuery,
    private readonly errorHandlerService: ErrorHandlerService,
  ) {}

  fetch(id: string) {
    this.store.setLoading(true);

    return this.sourceHttpService.fetch(id).pipe(
      this.errorHandlerService.setHttpErrorMetadata({ entity: 'source', action: 'fetch' }),
      map((sourceHttpModel) => this.toStoreModel(sourceHttpModel)),
      tap((sourceStoreModel) => {
        this.store.upsertMany([sourceStoreModel]);
      }),
      tap(() => {
        this.store.setActive(id);
      }),
      finalize(() => this.store.setLoading(false)),
      this.errorHandlerService.catchError(
        '[ReferenceQuestionService]: Fetching reference questions failed',
        rethrowError(),
        {
          toast: (err) => ({
            title: 'An error occurred while fetching the reference question',
            description: (err as Error)?.message,
          }),
        },
      ),
    );
  }

  fetchPaginatedListView(
    selectQueryConfig: SupabaseSelectQueryConfig = {},
  ): Observable<{ data: SourceListItemHttpModel[]; totalCount: number }> {
    return this.sourceHttpService
      .fetchPaginatedListView(selectQueryConfig)
      .pipe(this.errorHandlerService.setHttpErrorMetadata({ entity: 'source', action: 'fetch' }));
  }

  create({ name, syllabus_id }: Pick<SourceCreateModel, 'name' | 'syllabus_id'>) {
    const source: SourceCreateModel = {
      name,
      source_type: 'OFFICIAL_PAST_EXAM',
      document_type: 'PDF',
      raw_source: [],
      syllabus_id,
      status: 'DRAFT',
    };

    return this.sourceHttpService.create(source).pipe(
      this.errorHandlerService.setHttpErrorMetadata({ entity: 'source', action: 'create' }),
      tap((createdSource) => {
        this.store.create(this.toStoreModel(createdSource));
      }),
    );
  }

  update(id: string, source: SourceUpdateModel) {
    return this.sourceHttpService.update(id, source).pipe(
      this.errorHandlerService.setHttpErrorMetadata({ entity: 'source', action: 'update' }),
      tap((updatedSource) => {
        this.store.update(id, this.toStoreModel(updatedSource));
      }),
      mapToVoid(),
    );
  }

  delete(id: string): Observable<void> {
    return this.sourceHttpService.delete(id).pipe(
      this.errorHandlerService.setHttpErrorMetadata({ entity: 'source', action: 'delete' }),
      tap(() => {
        this.store.delete(id);
      }),
      mapToVoid(),
    );
  }

  async getRawSourceUrls(pdfPlace: RawSourceItem['type'], source = this.query.getActive()) {
    if (!source) {
      return [];
    }

    const rawSources = source.raw_source.filter((ref) => ref.type === pdfPlace);

    if (rawSources.length === 0) {
      return Promise.resolve([]);
    }

    return this.sourceHttpService.getSignedUrls(rawSources.map((ref) => ref.key));
  }

  private toStoreModel(source: SourceHttpModel): SourceStoreModel {
    return {
      ...source,
      raw_source: source.raw_source as unknown as SourceStoreModel['raw_source'],
      processed_content: source.processed_content as unknown as SourceStoreModel['processed_content'],
      metadata: source.metadata as unknown as SourceStoreModel['metadata'],
    };
  }
}
